import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, Target, Sparkles, Flame, TrendingUp, Award, Lock, LogIn, Zap } from "lucide-react";
import { useAuth } from "../components/AuthProvider";
import { primeReasonLabel } from "../data/primeReasons";
import { getMashFactor } from "../data/mashFactors";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  bronze: { bg: "#7A3E1F", ring: "#B87333", text: "#FFD9AA" },
  silver: { bg: "#4B4B57", ring: "#B0B0B8", text: "#F1F1F5" },
  gold: { bg: "#7A5F00", ring: "#FFD700", text: "#FFF6C8" },
  platinum: { bg: "#164E63", ring: "#00E5FF", text: "#E4FBFF" },
};

const CATEGORY_LABELS = {
  music: "Music", movie: "Movie", sports: "Sports",
  historical: "Historical", business: "Business", mixed: "Mixed",
};

export default function MyStats() {
  const nav = useNavigate();
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState(null);
  const [power, setPower] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading || !user) return;
    fetch(`${API}/api/users/me/stats`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => setErr("Couldn't load your stats."));
    fetch(`${API}/api/users/me/daily`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then(setDaily)
      .catch(() => {});
    fetch(`${API}/api/users/me/power`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then(setPower)
      .catch(() => {});
  }, [loading, user]);

  if (loading) {
    return <div className="min-h-screen spotlight grain scanlines pt-24 text-center text-[#FDFBF7]/60">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-16 pb-nav text-center">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-[#FF107A]/15 border border-[#FF107A]/40 items-center justify-center mb-6">
            <Lock size={26} className="text-[#FF107A]" />
          </div>
          <h1 className="font-display text-5xl leading-[0.9] tracking-tight">My Stats</h1>
          <p className="mt-3 text-sm text-[#FDFBF7]/70 max-w-xs mx-auto">
            Sign in to build a persistent Prime Rating, unlock badges, and climb the Oracle tier.
          </p>
          <button
            data-testid="stats-signin-btn"
            onClick={login}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#0A090D] px-6 py-3 font-bold tracking-wide"
          >
            <LogIn size={16} /> Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (err) {
    return <div className="min-h-screen pt-16 text-center text-[#FF107A]">{err}</div>;
  }

  if (!data) {
    return <div className="min-h-screen spotlight grain scanlines pt-24 text-center text-[#FDFBF7]/60">Building your profile…</div>;
  }

  const { stats, achievements, user: u } = data;

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
        <button data-testid="stats-back-btn" onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Home
        </button>

        {/* Identity card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          data-testid="stats-identity-card"
          className="mt-6 rounded-3xl border border-white/10 p-5 relative overflow-hidden"
          style={{ background: "radial-gradient(circle at 20% 10%, rgba(255,16,122,0.25), transparent 60%), #16141D" }}
        >
          <div className="absolute inset-0 halftone opacity-15 pointer-events-none" />
          <div className="relative flex items-center gap-4">
            {u.picture ? (
              <img src={u.picture} alt="" className="w-16 h-16 rounded-2xl border-2 border-[#FF107A]/60" />
            ) : (
              <div className="w-16 h-16 rounded-2xl border-2 border-[#FF107A]/60 bg-[#FF107A]/20 flex items-center justify-center font-display text-3xl text-[#FF107A]">
                {u.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-display text-3xl leading-none tracking-tight truncate">{u.name}</div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#FFD700] font-bold" data-testid="stats-title">{stats.title}</span>
                <span className="text-[#FDFBF7]/40">·</span>
                <span className="text-xs text-[#FDFBF7]/70">Rank <span className="text-[#FDFBF7] font-bold" data-testid="stats-global-rank">#{stats.global_rank}</span></span>
              </div>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <IdStat label="Rating" value={stats.prime_rating} testId="stats-rating" accent="#FFD700" />
            <IdStat label="Accuracy" value={`${stats.accuracy}%`} testId="stats-accuracy" accent="#FF107A" />
            <IdStat label="Prime Matches" value={stats.prime_matches} testId="stats-primes" accent="#00E5FF" />
          </div>
        </motion.div>

        {/* Prime section */}
        <Section title="Prime Metrics" icon={Target} accent="#FF107A">
          <div className="grid grid-cols-2 gap-3">
            <MiniStat label="Challenges Created" value={stats.sent} testId="stats-created" />
            <MiniStat label="Challenges Played" value={stats.completed_by_user} testId="stats-played" />
            <MiniStat label="Perfect Predictions" value={stats.perfect_predictions} testId="stats-perfect" />
            <MiniStat label="Highest Score" value={stats.highest_score} testId="stats-high-score" />
            <MiniStat label="Savant Ratings" value={stats.savant_count} testId="stats-savant" />
            <MiniStat label="Oracle Ratings" value={stats.oracle_count} testId="stats-oracle" />
          </div>
        </Section>

        {/* Personality */}
        <Section title="Your Personality" icon={Sparkles} accent="#A855F7">
          <div className="space-y-2 text-sm">
            <PersonalityRow label="Favorite category" value={stats.favorite_category ? CATEGORY_LABELS[stats.favorite_category] || stats.favorite_category : "—"} testId="stats-fav-cat" />
            <PersonalityRow label="Most-used Mash Factor" value={stats.most_used_factor ? getMashFactor(stats.most_used_factor).label : "—"} testId="stats-fav-factor" />
            <PersonalityRow label="Most-predicted reason" value={stats.most_used_reason ? primeReasonLabel(stats.most_used_reason) : "—"} testId="stats-fav-reason" />
          </div>
        </Section>

        {/* Category breakdown */}
        {(stats.category_breakdown || []).length > 0 && (
          <Section title="By Category" icon={TrendingUp} accent="#00E5FF">
            <div className="space-y-2" data-testid="stats-category-breakdown">
              {stats.category_breakdown.map((c) => (
                <div key={c.category} className="rounded-xl border border-white/10 bg-black/30 px-3 py-2.5">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="font-semibold text-[#FDFBF7]">{CATEGORY_LABELS[c.category] || c.category}</span>
                    <span className="text-[#FDFBF7]/70">{c.won}/{c.played} · <span className="text-[#00E5FF]">{c.accuracy}%</span></span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-[#00E5FF]" style={{ width: `${c.accuracy}%`, boxShadow: "0 0 8px #00E5FF" }} />
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Power Smash stats */}
        {power && power.games_played > 0 && (
          <Section title={`Power Smash · ${power.title}`} icon={Zap} accent="#FFD700">
            <div className="grid grid-cols-3 gap-2" data-testid="stats-power-grid">
              <MiniStat label="Games" value={power.games_played} testId="stats-power-games" />
              <MiniStat label="Highest" value={power.highest_score} testId="stats-power-highest" />
              <MiniStat label="Avg" value={power.avg_score} testId="stats-power-avg" />
              <MiniStat label="Accuracy" value={`${power.accuracy_pct}%`} testId="stats-power-acc" />
              <MiniStat label="Perfect" value={power.perfect_rounds} testId="stats-power-perfect" />
              <MiniStat label="Trivia" value={`${power.trivia_accuracy}%`} testId="stats-power-trivia" />
            </div>
            {power.favorite_prime && (
              <div className="mt-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm">
                Favorite Prime: <span className="text-[#FFD700] font-bold">{power.favorite_prime.name}</span>
              </div>
            )}
            {power.achievements?.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="stats-power-achievements">
                {power.achievements.map((b) => {
                  const t = TIER_COLORS[b.tier] || TIER_COLORS.bronze;
                  return (
                    <div key={b.id} data-testid={`stats-power-badge-${b.id}`}
                      className="aspect-square rounded-2xl p-2 flex flex-col items-center justify-center text-center border-2"
                      style={{ background: t.bg, borderColor: t.ring, boxShadow: `0 0 12px ${t.ring}55` }}
                      title={b.desc}>
                      <Zap size={16} style={{ color: t.ring }} />
                      <div className="font-display text-[10px] leading-tight mt-1" style={{ color: t.text }}>{b.name}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        )}

        {/* Reward gallery — streak-tier unlockables */}        {daily?.reward_tiers?.length > 0 && (
          <Section title={`Streak Rewards · ${daily.rewards.length}/${daily.reward_tiers.length}`} icon={Flame} accent="#FF107A">
            <div className="grid grid-cols-3 gap-2" data-testid="stats-rewards-gallery">
              {daily.reward_tiers.map((t) => {
                const unlocked = daily.rewards.some((r) => r.id === t.id);
                return (
                  <div
                    key={t.id}
                    data-testid={`stats-reward-${t.id}-${unlocked ? "unlocked" : "locked"}`}
                    className="aspect-square rounded-2xl p-2 flex flex-col items-center justify-center text-center border-2 relative overflow-hidden"
                    style={{
                      background: unlocked ? `${t.color}22` : "#1A1820",
                      borderColor: unlocked ? t.color : "#2A2830",
                      boxShadow: unlocked ? `0 0 16px ${t.color}66` : "none",
                      filter: unlocked ? "none" : "grayscale(1) opacity(0.55)",
                    }}
                    title={unlocked ? `Unlocked at ${t.tier}-day streak` : `Reach a ${t.tier}-day streak to unlock`}
                  >
                    {unlocked ? (
                      <Flame size={18} style={{ color: t.color }} />
                    ) : (
                      <Lock size={16} className="text-[#FDFBF7]/60" />
                    )}
                    <div
                      className="font-display text-[11px] leading-tight mt-1.5"
                      style={{ color: unlocked ? t.color : "#FDFBF7AA" }}
                    >
                      {t.name}
                    </div>
                    <div className="text-[9px] tracking-[0.15em] uppercase text-[#FDFBF7]/60 mt-0.5">
                      {t.tier}d
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/50 text-center">
              Current streak: <span className="text-[#FF107A] font-bold">{daily.daily_streak}d</span>
              {" · "}Longest: <span className="text-[#FDFBF7] font-bold">{daily.longest_streak}d</span>
            </div>
          </Section>
        )}

        {/* Achievements */}
        <Section title={`Achievements · ${achievements.length}`} icon={Award} accent="#FFD700">
          {achievements.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-black/30 px-4 py-6 text-center text-sm text-[#FDFBF7]/50">
              Play a Prime Challenge to earn your first badge.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2" data-testid="stats-achievements">
              {achievements.map((b) => {
                const t = TIER_COLORS[b.tier] || TIER_COLORS.bronze;
                return (
                  <div
                    key={b.id}
                    data-testid={`stats-badge-${b.id}`}
                    className="aspect-square rounded-2xl p-2 flex flex-col items-center justify-center text-center border-2"
                    style={{ background: t.bg, borderColor: t.ring, boxShadow: `0 0 12px ${t.ring}55` }}
                    title={b.desc}
                  >
                    <Trophy size={18} style={{ color: t.ring }} />
                    <div className="font-display text-[11px] leading-tight mt-1.5" style={{ color: t.text }}>{b.name}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* CTA */}
        <button
          data-testid="stats-view-wrapped-btn"
          onClick={() => nav("/wrapped")}
          className="mt-6 w-full rounded-full border border-[#FFD700]/50 bg-[#FFD700]/10 text-[#FFD700] py-3 font-bold flex items-center justify-center gap-2"
        >
          <Sparkles size={16} /> View Your Prime Wrapped
        </button>
        <button
          data-testid="stats-view-leaderboard-btn"
          onClick={() => nav("/leaderboards/prime")}
          className="mt-3 w-full rounded-full border border-white/15 bg-black/30 text-[#FDFBF7] py-3 font-bold flex items-center justify-center gap-2"
        >
          <Trophy size={16} /> See the Leaderboard
        </button>
        <button
          data-testid="stats-new-challenge-btn"
          onClick={() => nav("/challenge/new")}
          className="mt-3 w-full rounded-full bg-[#FF107A] text-[#FDFBF7] py-3 font-bold glow-pink flex items-center justify-center gap-2"
        >
          <Flame size={16} /> Send another Prime Challenge
        </button>
      </div>
    </div>
  );
}

const Section = ({ title, icon: Icon, accent, children }) => (
  <div className="mt-6">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-bold mb-2" style={{ color: accent }}>
      <Icon size={12} /> {title}
    </div>
    {children}
  </div>
);

const IdStat = ({ label, value, testId, accent }) => (
  <div className="rounded-xl bg-black/40 border border-white/10 px-2 py-2.5 text-center">
    <div className="font-display text-2xl leading-none tabular" style={{ color: accent }} data-testid={testId}>{value}</div>
    <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/60 mt-1">{label}</div>
  </div>
);

const MiniStat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-white/10 bg-[#16141D]/80 px-3 py-3">
    <div className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/60">{label}</div>
    <div className="font-display text-3xl leading-none tabular mt-1 text-[#FDFBF7]" data-testid={testId}>{value}</div>
  </div>
);

const PersonalityRow = ({ label, value, testId }) => (
  <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 flex justify-between items-center">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/60">{label}</span>
    <span className="text-sm font-semibold text-[#FDFBF7] truncate ml-2 max-w-[60%] text-right" data-testid={testId}>{value}</span>
  </div>
);
